import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Search, Check, MapPin, Star, ArrowRight } from "lucide-react";
import { PROJECT_FOLDER_COLORS, tint, TuckedProCard } from "@/components/ProjectFolderCard";

/** Step 2 of the project flow — "Choose from My Board". */
export default function ChooseFromBoardPanel({ projectId, project, existingProIds, onClose, onAdded }) {
  const [pins, setPins] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("__all__");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/boards/me").then(r => setPins(r.data.pins || [])).catch(() => {});
  }, []);

  const categories = useMemo(() => {
    const set = new Set(pins.map(p => p.category).filter(Boolean));
    return ["__all__", ...Array.from(set)];
  }, [pins]);

  const available = useMemo(() => {
    return pins.filter(p =>
      !(existingProIds || []).includes(p.user_id) && !p.inactive &&
      (category === "__all__" || p.category === category) &&
      (!query.trim() || p.business_name?.toLowerCase().includes(query.trim().toLowerCase()))
    );
  }, [pins, existingProIds, category, query]);

  const selectedPros = useMemo(() => pins.filter(p => selected.includes(p.user_id)), [pins, selected]);

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const addSelected = async () => {
    if (selected.length === 0) return;
    setBusy(true);
    try {
      for (const proId of selected) {
        await api.post(`/projects/${projectId}/pros`, { pro_user_id: proId });
      }
      toast.success(`Added ${selected.length} pro${selected.length === 1 ? "" : "s"} to the project`);
      onAdded();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
    finally { setBusy(false); }
  };

  const base = PROJECT_FOLDER_COLORS[project?.color_id] || PROJECT_FOLDER_COLORS.sage;
  const back = tint(base, 0.42);
  const front = tint(base, 0.62);
  const name = project?.name || "this project";

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] grid place-items-center p-4 overflow-y-auto" onClick={onClose} data-testid="choose-from-board-panel">
      <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl my-8" onClick={e => e.stopPropagation()}>

        <div className="px-7 pt-6 pb-4 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-[22px] font-bold text-[#2D2926]" style={{ fontFamily: "Outfit" }} data-testid="choose-from-board-title">
              Add professionals to {name}
            </h3>
            <p className="text-sm text-[#2D2926]/55 mt-0.5">Choose from your pinned pros on My Board.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-black/5 shrink-0" data-testid="choose-from-board-close"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-0 border-t border-black/[0.06]">

          {/* left — live folder preview */}
          <div className="p-6 md:border-r border-black/[0.06]">
            <div className="relative h-[250px]" data-testid="choose-from-board-preview">
              <div className="absolute top-0 left-4 w-[130px] h-6 rounded-t-[10px]" style={{ background: back }} />
              <div className="absolute left-0 right-0 top-5 bottom-[6px] rounded-tr-[14px] rounded-b-[14px] rounded-tl-[4px]" style={{ background: back }} />
              <div className="absolute left-4 right-4 top-9">
                {selectedPros.length === 0 ? (
                  <span className="text-[11px] text-[#2D2926]/45 italic">Select pros to see them tucked in</span>
                ) : (
                  <div className="relative h-[86px]">
                    {selectedPros.slice(0, 3).map((p, i) => (
                      <TuckedProCard
                        key={p.user_id}
                        pro={p}
                        rotate={[-6, 3, -2][i]}
                        className={`absolute ${["left-0 top-0", "left-8 top-6", "left-3 top-12"][i]}`}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div
                className="absolute left-0 right-0 bottom-0 h-[112px] rounded-b-[16px] rounded-t-[10px] px-5 pt-4"
                style={{ background: `linear-gradient(180deg, ${front} 0%, ${tint(base, 0.52)} 100%)`, borderTop: "1px solid rgba(255,255,255,0.55)" }}
              >
                <p className="text-[18px] font-bold text-[#2D2926] truncate" style={{ fontFamily: "Outfit" }}>{name}</p>
                <p className="text-[13px] text-[#2D2926]/60 mt-1">
                  {selectedPros.length === 0 ? "0 professionals yet" : `${selectedPros.length} professional${selectedPros.length === 1 ? "" : "s"} selected`}
                </p>
                <p className="text-[13px] text-[#2D2926]/55 flex items-center gap-1.5 mt-0.5">
                  Add pros from your board <ArrowRight size={13} />
                </p>
              </div>
            </div>
          </div>

          {/* right — pinned pro picker */}
          <div className="p-6">
            <div className="flex gap-2 mb-3">
              <div className="relative flex-1 min-w-0">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2D2926]/40" />
                <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search your pinned pros…"
                  className="w-full pl-8 pr-3 py-2.5 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]/40 focus:border-[#2E6B57]" data-testid="choose-from-board-search" />
              </div>
              <select value={category} onChange={e => setCategory(e.target.value)} className="px-3 py-2.5 border border-black/10 rounded-xl text-sm bg-white shrink-0 max-w-[150px]" data-testid="choose-from-board-category">
                {categories.map(c => <option key={c} value={c}>{c === "__all__" ? "All categories" : c}</option>)}
              </select>
            </div>

            <div className="max-h-[330px] overflow-y-auto divide-y divide-black/[0.05] border border-black/[0.06] rounded-xl" data-testid="choose-from-board-list">
              {available.length === 0 ? (
                <p className="text-sm text-[#2D2926]/50 text-center py-10">No matching pinned pros.</p>
              ) : available.map(p => {
                const on = selected.includes(p.user_id);
                return (
                  <button key={p.user_id} onClick={() => toggle(p.user_id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition ${on ? "bg-[#E9F0EC]" : "hover:bg-black/[0.02]"}`}
                    data-testid={`choose-pro-${p.user_id}`} aria-pressed={on}>
                    <span className={`w-5 h-5 rounded-[5px] border-2 grid place-items-center shrink-0 ${on ? "bg-[#245544] border-[#245544]" : "border-black/20"}`}>
                      {on && <Check size={13} className="text-white" strokeWidth={3} />}
                    </span>
                    <div className="w-9 h-9 rounded-full bg-[#2E6B57] grid place-items-center text-xs font-bold text-white overflow-hidden shrink-0">
                      {p.avatar_url ? <img src={p.avatar_url} className="w-full h-full object-cover" alt="" /> : p.business_name?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate text-[#2D2926]" style={{ fontFamily: "Outfit" }}>{p.business_name}</p>
                      <p className="text-xs text-[#2D2926]/50 truncate">{p.category}</p>
                    </div>
                    {p.city && <span className="hidden sm:flex items-center gap-1 text-xs text-[#2D2926]/50 shrink-0"><MapPin size={11} /> {p.city}</span>}
                    {p.review_count > 0 && (
                      <span className="flex items-center gap-1 text-xs font-semibold text-[#2D2926]/70 shrink-0">
                        <Star size={11} className="fill-current text-amber-400" /> {p.rating} <span className="font-normal text-[#2D2926]/40">({p.review_count})</span>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="px-7 py-4 border-t border-black/[0.06] flex items-center justify-between gap-3">
          <span className="text-sm text-[#2D2926]/60" data-testid="choose-from-board-selected-count">{selected.length} selected</span>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-black/10 text-sm font-semibold hover:bg-black/[0.03]" data-testid="choose-from-board-cancel">Cancel</button>
            <button onClick={addSelected} disabled={busy || selected.length === 0} className="btn-lime px-5 py-2.5 text-sm disabled:opacity-40" data-testid="choose-from-board-add">
              Add {selected.length > 0 ? `${selected.length} ` : ""}selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
